'use no memo'

import { flexRender, type Row, type RowData, type Table as TableType } from '@tanstack/react-table'
import type { ReactNode } from 'react'
import { Bone } from '~/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { cn } from '~/lib/utils'

declare module '@tanstack/react-table' {
  // L'augmentation reprend les paramètres de TanStack Table, à l'identique, même inutilisés.
  // oxlint-disable-next-line no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Cellule de la colonne pendant le chargement (`AppTableData loading`) ; un os de texte sinon. */
    skeleton?: ReactNode
  }
}

interface DataTableProps<TData> {
  table: TableType<TData>
}

/** Largeurs des os de texte, en part de la cellule : une suite fixe, pour que les lignes varient. */
const BONE_WIDTHS = ['64%', '48%', '72%', '56%', '40%']

type AppTableVariant = 'default' | 'green'

type AppTableColumnSizing = 'all' | 'only-explicit' | 'none'

interface AppTableProps {
  variant?: AppTableVariant
}

export const AppTable = ({ children }: React.PropsWithChildren<AppTableProps>) => (
  <div className="flex flex-col gap-4 rounded-3xl border bg-background p-4">{children}</div>
)

export const AppTableData = <TData,>({
  table,
  variant = 'default',
  columnSizing = 'all',
  tableClassName,
  wrapperClassName,
  rowClassName,
  onRowClick,
  isRowClickable = () => true,
  loading = false,
  skeletonRows = 5,
}: DataTableProps<TData> &
  AppTableProps & {
    columnSizing?: AppTableColumnSizing
    tableClassName?: string
    /** Classes supplémentaires du cadre du tableau (coins, bordure). */
    wrapperClassName?: string
    /** Classes supplémentaires d'une ligne (ex. surface d'alerte d'une ligne à traiter). */
    rowClassName?: (row: Row<TData>) => string | undefined
    /** Rend chaque ligne cliquable (curseur + clavier) — pour ouvrir un panneau de détail. */
    onRowClick?: (row: Row<TData>) => void
    /** Lignes que `onRowClick` concerne (toutes par défaut) : les autres restent inertes. */
    isRowClickable?: (row: Row<TData>) => boolean
    /**
     * Chargement : les vrais en-têtes, puis `skeletonRows` lignes dont chaque cellule est
     * `columnDef.meta.skeleton` (un os de texte sans lui), jamais « Aucun résultat ».
     */
    loading?: boolean
    skeletonRows?: number
  }): React.JSX.Element => {
  const headerClassName = variant === 'green' ? 'bg-[#F6F8F5]' : undefined
  const headerCellClassName = variant === 'green' ? 'py-4' : undefined

  interface SizedColumn {
    getSize: () => number
    columnDef: {
      size?: unknown
    }
  }

  const getCellWidthStyle = (
    column: SizedColumn,
  ): { width: number; minWidth: number; maxWidth: number } | undefined => {
    if (columnSizing === 'none') {
      return undefined
    }

    const definedSize = column.columnDef.size
    const hasDefinedSize = typeof definedSize === 'number'
    if (columnSizing === 'only-explicit' && !hasDefinedSize) {
      return undefined
    }

    const size = column.getSize()
    return {
      width: size,
      minWidth: size,
      maxWidth: size,
    }
  }

  const { rows } = table.getRowModel()

  return (
    <div className={cn('overflow-hidden rounded-2xl border', wrapperClassName)}>
      <Table className={cn('table-fixed', tableClassName)}>
        <TableHeader className={headerClassName}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={cn(headerCellClassName)}
                  style={getCellWidthStyle(header.column)}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {/* Chargement : par colonne visible, `meta.skeleton` ou un os de texte. */}
          {loading &&
            Array.from({ length: skeletonRows }, (_, rowIndex) => (
              <TableRow key={rowIndex} className="hover:bg-transparent">
                {table.getVisibleLeafColumns().map((column, columnIndex) => (
                  <TableCell key={column.id} style={getCellWidthStyle(column)}>
                    {column.columnDef.meta?.skeleton ?? (
                      <Bone width={BONE_WIDTHS[(rowIndex + columnIndex) % BONE_WIDTHS.length]} />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          {!loading &&
            rows.map((row) => {
              const onClick = onRowClick && isRowClickable(row) ? onRowClick : undefined
              return (
                <TableRow
                  data-state={row.getIsSelected() && 'selected'}
                  key={row.id}
                  className={cn(onClick && 'cursor-pointer', rowClassName?.(row))}
                  onClick={onClick ? () => onClick(row) : undefined}
                  onKeyDown={
                    onClick
                      ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            onClick(row)
                          }
                        }
                      : undefined
                  }
                  tabIndex={onClick ? 0 : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} style={getCellWidthStyle(cell.column)}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          {!loading && rows.length === 0 && (
            <TableRow>
              <TableCell className="h-24 text-center" colSpan={table.getAllColumns().length}>
                Aucun résultat.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
